import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCustomers } from '../../../apis/customers';
import { getAacBlockTestRequest, createAacBlockTestRequest, updateAacBlockTestRequest, saveAacBlockObservations } from '../../../apis/aacBlock';
import { 
  faCube, 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faInfoCircle,
  faFlask,
  faTable,
  faUserCheck,
  faComment,
  faFileAlt,
  faImage,
  faUpload,
  faCamera
} from '@fortawesome/free-solid-svg-icons';

// Parse DD/MM/YYYY to YYYY-MM-DD for date inputs
const parseDateToInput = (str) => {
  if (!str) return '';
  if (str.includes('-') && str.length === 10 && str.charAt(4) === '-') return str; // already YYYY-MM-DD
  const parts = String(str).split(/[/-]/);
  if (parts.length === 3) {
    const [d, m, y] = parts;
    if (y && y.length === 4) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
};

const AACBlocksForm = () => {
  const [searchParams] = useSearchParams();
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingTest, setLoadingTest] = useState(false);
  const [savedTestRequestId, setSavedTestRequestId] = useState(null);
  const [formData, setFormData] = useState({
    // General Information (constants from observation sheet & test report)
    customer_id: '',
    sample_description: 'ACC Block',
    date_of_receipt: '',
    customer_site_name_address: '',
    reference_number: '',
    date_of_manufacturer: '',
    type_of_specimen: 'ACC Block',
    machine_used_for_testing: 'CTM (2000KN)',
    location_of_test: 'Lab',
    capacity_range: '2000KN',
    test_method: 'IS 6441 (PART 5)-1972',
    
    // Sample Details
    sample_test_code: '',
    date_of_testing: '',
    date_of_report: '',
    ulr_number: '',
    job_code_number: '',
    manufacturer: '',
    condition_of_specimen: 'Acceptable',
    calibration_due_date: '2026-07-01',
    environmental_condition: 'Not Applicable',
    
    // AAC Block Specific Details
    quantity_of_blocks: '',
    date_of_casting: '',
    grade_of_blocks: '',
    manufacture_of_blocks: '',
    blocks_condition: 'Acceptable',
    curing_condition: '',
    
    // Test Results
    block_id_1: '', length_1: '', breadth_1: '', height_1: '', area_1: '', weight_1: '', density_1: '', load_max_1: '', compressive_strength_1: '',
    block_id_2: '', length_2: '', breadth_2: '', height_2: '', area_2: '', weight_2: '', density_2: '', load_max_2: '', compressive_strength_2: '',
    block_id_3: '', length_3: '', breadth_3: '', height_3: '', area_3: '', weight_3: '', density_3: '', load_max_3: '', compressive_strength_3: '',
    avg_load_max: '',
    avg_compressive_strength: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  // Customer data state (like Liquid Admixture - Relative Density Test)
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');

  // Block images for test report (Front, Digital Reading, Back per block 1–3)
  const [blockImages, setBlockImages] = useState({});
  const [isCapturing, setIsCapturing] = useState(false);
  const [currentFacingMode, setCurrentFacingMode] = useState('environment');

  // Load saved AAC Block test when opened from dashboard (View/Edit)
  useEffect(() => {
    const id = searchParams.get('testRequestId');
    if (!id) return;
    const numId = parseInt(id, 10);
    if (Number.isNaN(numId)) return;
    setLoadingTest(true);
    getAacBlockTestRequest(numId)
      .then((res) => {
        setSavedTestRequestId(res.id);
        const obs = res.observation || {};
        const fd = obs.form_data || {};
        const dateFields = ['date_of_receipt', 'date_of_testing', 'date_of_report', 'date_of_manufacturer', 'date_of_casting', 'tested_by_date', 'checked_by_date', 'verified_by_date', 'calibration_due_date'];
        const normalized = {
          ...fd,
          customer_id: fd.customer_id || res.customer_id || '',
          customer_site_name_address: fd.customer_site_name_address || res.site_name || '',
          ulr_number: fd.ulr_number || res.ulr_number || '',
          job_code_number: fd.job_code_number || res.job_number || ''
        };
        dateFields.forEach((key) => {
          if (normalized[key]) normalized[key] = parseDateToInput(normalized[key]) || normalized[key];
        });
        setFormData((prev) => ({ ...prev, ...normalized }));
        if (obs.block_images && typeof obs.block_images === 'object') {
          setBlockImages(obs.block_images);
        }
        setCustomerSearchTerm(res.customer_name || fd.customer_name || '');
      })
      .catch(() => {})
      .finally(() => setLoadingTest(false));
  }, [searchParams]);

  // Resize canvas for capture (like cube testing)
  const resizeImageForCapture = (canvas, maxWidth = 800, maxHeight = 600) => {
    let width = canvas.width;
    let height = canvas.height;
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
      const resized = document.createElement('canvas');
      resized.width = width;
      resized.height = height;
      resized.getContext('2d').drawImage(canvas, 0, 0, width, height);
      return resized;
    }
    return canvas;
  };

  // Compress image for URL-safe size (report loads images from URL params)
  const compressBlockImage = (dataURL) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 800;
        const maxH = 600;
        let w = img.width;
        let h = img.height;
        if (w > maxW || h > maxH) {
          const r = Math.min(maxW / w, maxH / h);
          w = Math.floor(w * r);
          h = Math.floor(h * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL('image/jpeg', 0.65);
        resolve(out);
      };
      img.onerror = () => resolve(dataURL);
      img.src = dataURL;
    });
  };

  const handleBlockImageUpload = (blockNum, imageType, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataURL = e.target.result;
      const compressed = await compressBlockImage(dataURL);
      const key = `${imageType}_${blockNum}`;
      setBlockImages(prev => ({ ...prev, [key]: compressed }));
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteBlockImage = (key) => {
    setBlockImages(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Camera (like cube testing)
  const openAacCamera = (photoKey) => {
    const modal = document.getElementById('aacCameraModal');
    if (modal) {
      modal.setAttribute('data-photo-type', photoKey);
      modal.style.display = 'flex';
      startAacCamera();
    }
  };

  const startAacCamera = async (facingMode = 'environment') => {
    try {
      const video = document.getElementById('aacCameraVideo');
      if (!video) return;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode }
      });
      video.srcObject = stream;
      setCurrentFacingMode(facingMode);
    } catch (err) {
      console.error('Camera error:', err);
      alert('Camera access denied. Please allow camera permissions.');
    }
  };

  const switchAacCamera = () => {
    const newMode = currentFacingMode === 'environment' ? 'user' : 'environment';
    closeAacCamera();
    setTimeout(() => startAacCamera(newMode), 100);
  };

  const closeAacCamera = () => {
    const modal = document.getElementById('aacCameraModal');
    const video = document.getElementById('aacCameraVideo');
    if (modal) modal.style.display = 'none';
    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  const captureAacPhoto = () => {
    const video = document.getElementById('aacCameraVideo');
    const canvas = document.getElementById('aacCameraCanvas');
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    const resized = resizeImageForCapture(canvas, 800, 600);
    let imageData = resized.toDataURL('image/jpeg', 0.7);
    const modal = document.getElementById('aacCameraModal');
    const photoKey = modal ? modal.getAttribute('data-photo-type') : null;
    if (!photoKey) return;
    setIsCapturing(true);
    setTimeout(() => {
      setBlockImages(prev => ({ ...prev, [photoKey]: imageData }));
      setIsCapturing(false);
      closeAacCamera();
    }, 800);
  };

  // Fetch customers on mount (like Liquid Admixture)
  useEffect(() => {
    const fetchCustomersData = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError(null);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomersError('Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomersData();
  }, []);

  // Filter customers by dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
        (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown]);

  const handleCustomerSelect = (customer) => {
    const siteAddress = [customer.site_name, customer.address].filter(Boolean).join(', ');
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id.toString(),
      customer_site_name_address: siteAddress || prev.customer_site_name_address
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm('');
  };

  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm('');
  };

  // Add style CSS for Form.Select dropdowns
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Style for Form.Select dropdowns - grey background for better visibility */
      .form-select {
        background-color: #495057 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
      }
      
      .form-select:focus {
        background-color: #495057 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .form-select option {
        background-color: #495057 !important;
        color: white !important;
      }
      
      /* Style for table-info rows - prevent light blue hover */
      .table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info td {
        background-color: #495057 !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      .table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info td {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateArea = (rowNumber) => {
    const length = parseFloat(formData[`length_${rowNumber}`]) || 0;
    const breadth = parseFloat(formData[`breadth_${rowNumber}`]) || 0;
    const area = length * breadth;
    
    setFormData(prev => ({
      ...prev,
      [`area_${rowNumber}`]: area.toFixed(2)
    }));
  };

  const calculateDensity = (rowNumber) => {
    const length = parseFloat(formData[`length_${rowNumber}`]) || 0;
    const breadth = parseFloat(formData[`breadth_${rowNumber}`]) || 0;
    const height = parseFloat(formData[`height_${rowNumber}`]) || 0;
    const weight = parseFloat(formData[`weight_${rowNumber}`]) || 0;
    
    if (length > 0 && breadth > 0 && height > 0 && weight > 0) {
      const volume = (length * breadth * height) / 1000000000; // Convert mm³ to m³
      const density = weight / volume;
      
      setFormData(prev => ({
        ...prev,
        [`density_${rowNumber}`]: density.toFixed(1)
      }));
    }
  };

  const calculateAverages = () => {
    let loadMaxSum = 0;
    let compressiveStrengthSum = 0;
    let validLoadMaxCount = 0;
    let validCompressiveStrengthCount = 0;
    
    for (let row = 1; row <= 3; row++) {
      const loadMax = parseFloat(formData[`load_max_${row}`]) || 0;
      const compressiveStrength = parseFloat(formData[`compressive_strength_${row}`]) || 0;
      
      if (loadMax > 0) {
        loadMaxSum += loadMax;
        validLoadMaxCount++;
      }
      
      if (compressiveStrength > 0) {
        compressiveStrengthSum += compressiveStrength;
        validCompressiveStrengthCount++;
      }
    }
    
    const avgLoadMax = validLoadMaxCount > 0 ? loadMaxSum / validLoadMaxCount : 0;
    const avgCompressiveStrength = validCompressiveStrengthCount > 0 ? compressiveStrengthSum / validCompressiveStrengthCount : 0;
    
    setFormData(prev => ({
      ...prev,
      avg_load_max: avgLoadMax > 0 ? avgLoadMax.toFixed(1) : '',
      avg_compressive_strength: avgCompressiveStrength > 0 ? avgCompressiveStrength.toFixed(2) : ''
    }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    handleSaveTestData();
  };

  // Save to backend then show post-save screen (like Liquid Admixture – no Confirm & Save)
  const handleSaveTestData = async () => {
    if (!formData.customer_id) {
      alert('Please select a customer before saving.');
      return;
    }
    setIsSaving(true);
    try {
      const requestPayload = {
        customer_id: formData.customer_id,
        customer_name: customerSearchTerm || '',
        site_name: formData.customer_site_name_address || '',
        receipt_date: formData.date_of_receipt || '',
        reference_number: formData.reference_number || '',
        job_code_number: formData.job_code_number || '',
        job_number: formData.job_code_number || '',
        ulr_number: formData.ulr_number || ''
      };
      let testRequestId = savedTestRequestId;
      if (testRequestId) {
        await updateAacBlockTestRequest(testRequestId, requestPayload);
      } else {
        const createRes = await createAacBlockTestRequest(requestPayload);
        testRequestId = createRes.test_request_id || createRes.id;
        if (!testRequestId) throw new Error('No test request ID returned from server');
        setSavedTestRequestId(testRequestId);
        if (createRes.ulr_number) setFormData(prev => ({ ...prev, ulr_number: createRes.ulr_number }));
        if (createRes.job_number) setFormData(prev => ({ ...prev, job_code_number: createRes.job_number }));
      }
      const observationsPayload = {
        formData: formData,
        blockImages: blockImages,
        tested_by_name: formData.tested_by_name || '',
        tested_by_date: formData.tested_by_date || '',
        checked_by_name: formData.checked_by_name || '',
        checked_by_date: formData.checked_by_date || '',
        verified_by_name: formData.verified_by_name || '',
        verified_by_date: formData.verified_by_date || '',
        reviewed_by: formData.reviewed_by || '',
        authorized_by: formData.authorized_by || '',
        remarks: formData.remarks || ''
      };
      await saveAacBlockObservations(testRequestId, observationsPayload);
      setShowPreview(true);
    } catch (err) {
      console.error('Save failed:', err);
      alert(err?.message || 'Failed to save test data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditForm = () => {
    setShowPreview(false);
  };

  // Function to generate and open test report (data via sessionStorage to avoid HTTP 431 URL length limit)
  const handleViewTestReport = () => {
    const token = `aac_report_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      return dateStr.replace(/-/g, '/');
    };
    const payload = {
      formData: { ...formData },
      blockImages: { ...blockImages }
    };
    // Dates already in form as YYYY-MM-DD; report expects same or YYYY/MM/DD
    try {
      sessionStorage.setItem(token, JSON.stringify(payload));
      window.open(`/AACBlock/AACBlockTestReport.html?preview_token=${encodeURIComponent(token)}`, '_blank');
    } catch (e) {
      console.error('sessionStorage failed:', e);
      alert('Could not open report. Try again or reduce image sizes.');
    }
  };

  // Function to generate and open observation sheet (data via sessionStorage to avoid HTTP 431)
  const handleViewObservationSheet = () => {
    const token = `aac_obs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const payload = { formData: { ...formData } };
    try {
      sessionStorage.setItem(token, JSON.stringify(payload));
      window.open(`/AACBlock/AACBlockObservationSheet.html?preview_token=${encodeURIComponent(token)}`, '_blank');
    } catch (e) {
      console.error('sessionStorage failed:', e);
      alert('Could not open observation sheet. Try again.');
    }
  };

  // Preview Form Component
  const PreviewForm = () => (
    <div>
      {/* Header */}
            <div style={{ backgroundColor: '#ffc107', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCube} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    AAC Block Test - Preview
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    Data saved. Open Observation Sheet or Test Report below.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button 
                onClick={handleEditForm}
                variant="light"
                className="btn-vitrag-primary me-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Edit Form
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        {/* General Information Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  General Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Customer:</strong> {customerSearchTerm || formData.customer_id || 'Not selected'}</p>
                    <p><strong>Sample Description:</strong> {formData.sample_description}</p>
                    <p><strong>Date of Receipt:</strong> {formData.date_of_receipt}</p>
                    <p><strong>Customer/Site Name & Address:</strong> {formData.customer_site_name_address}</p>
                    <p><strong>Reference Number:</strong> {formData.reference_number}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Date of Manufacturer:</strong> {formData.date_of_manufacturer}</p>
                    <p><strong>Type of Specimen:</strong> {formData.type_of_specimen}</p>
                    <p><strong>Machine used for Testing:</strong> {formData.machine_used_for_testing}</p>
                    <p><strong>Location of Test:</strong> {formData.location_of_test}</p>
                    <p><strong>Capacity/Range:</strong> {formData.capacity_range}</p>
                    <p><strong>Test Method:</strong> {formData.test_method}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Sample Details Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faFlask} className="me-2" />
                  Sample Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Sample Test Code Number:</strong> {formData.sample_test_code}</p>
                    <p><strong>Date of Testing:</strong> {formData.date_of_testing}</p>
                    <p><strong>Date of Report:</strong> {formData.date_of_report}</p>
                    <p><strong>ULR Number:</strong> {formData.ulr_number}</p>
                    <p><strong>Job Code Number:</strong> {formData.job_code_number}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Manufacturer:</strong> {formData.manufacturer}</p>
                    <p><strong>Condition of Specimen:</strong> {formData.condition_of_specimen}</p>
                    <p><strong>Calibration Due Date:</strong> {formData.calibration_due_date}</p>
                    <p><strong>Environmental condition:</strong> {formData.environmental_condition}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* AAC Block Specific Details Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faCube} className="me-2" />
                  AAC Block Specific Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Quantity of Blocks:</strong> {formData.quantity_of_blocks}</p>
                    <p><strong>Date of Casting:</strong> {formData.date_of_casting}</p>
                    <p><strong>Grade of Blocks:</strong> {formData.grade_of_blocks}</p>
                    <p><strong>Manufacture of Blocks:</strong> {formData.manufacture_of_blocks}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Blocks Condition:</strong> {formData.blocks_condition}</p>
                    <p><strong>Curing Condition:</strong> {formData.curing_condition}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Test Results Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faTable} className="me-2" />
                  Test Results
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm" style={{ fontSize: '0.875rem' }}>
                    <thead className="table-dark">
                      <tr>
                        <th>Sr. No.</th>
                        <th>Blocks ID</th>
                        <th>L (mm)</th>
                        <th>B (mm)</th>
                        <th>H (mm)</th>
                        <th>Area (mm²)</th>
                        <th>Block Weight (Kg)</th>
                        <th>Density (Kg/m³)</th>
                        <th>Maximum Load (N)</th>
                        <th>Compressive Strength (MPa)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((row) => (
                        <tr key={row}>
                          <td>{row}</td>
                          <td>{formData[`block_id_${row}`]}</td>
                          <td>{formData[`length_${row}`]}</td>
                          <td>{formData[`breadth_${row}`]}</td>
                          <td>{formData[`height_${row}`]}</td>
                          <td>{formData[`area_${row}`]}</td>
                          <td>{formData[`weight_${row}`]}</td>
                          <td>{formData[`density_${row}`]}</td>
                          <td>{formData[`load_max_${row}`]}</td>
                          <td>{formData[`compressive_strength_${row}`]}</td>
                        </tr>
                      ))}
                      <tr className="table-secondary">
                        <td colSpan="8" className="text-end fw-bold">Average</td>
                        <td className="text-center fw-bold">{formData.avg_load_max}</td>
                        <td className="text-center fw-bold">{formData.avg_compressive_strength}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Verification Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                  Verification & Authorization
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <h6>Tested By</h6>
                    <p><strong>Name:</strong> {formData.tested_by_name}</p>
                    <p><strong>Date:</strong> {formData.tested_by_date}</p>
                  </Col>
                  <Col md={4}>
                    <h6>Checked By</h6>
                    <p><strong>Name:</strong> {formData.checked_by_name}</p>
                    <p><strong>Date:</strong> {formData.checked_by_date}</p>
                  </Col>
                  <Col md={4}>
                    <h6>Verified By</h6>
                    <p><strong>Name:</strong> {formData.verified_by_name}</p>
                    <p><strong>Date:</strong> {formData.verified_by_date}</p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={6}>
                    <h6>Reviewed By</h6>
                    <p><strong>Name:</strong> {formData.reviewed_by}</p>
                  </Col>
                  <Col md={6}>
                    <h6>Authorized By</h6>
                    <p><strong>Name:</strong> {formData.authorized_by}</p>
                  </Col>
                </Row>
                {formData.remarks && (
                  <Row className="mt-3">
                    <Col>
                      <h6>Remarks</h6>
                      <p>{formData.remarks}</p>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons – like Liquid Admixture: Observation Sheet, Test Report, Edit, Cancel */}
        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex flex-column flex-md-row justify-content-center gap-2 flex-wrap">
              <Button
                variant="info"
                size="lg"
                className="mb-2 mb-md-0"
                onClick={handleViewObservationSheet}
              >
                <FontAwesomeIcon icon={faTable} className="me-2" />
                AAC Block Observation Sheet
              </Button>
              <Button
                variant="warning"
                size="lg"
                className="mb-2 mb-md-0"
                onClick={handleViewTestReport}
                style={{ backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                AAC Block Test Report
              </Button>
              <Button 
                onClick={handleEditForm}
                variant="secondary" 
                size="lg"
                className="mb-2 mb-md-0"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Edit Form
              </Button>
              <Button 
                as={Link} 
                to="/other-services" 
                variant="danger" 
                size="lg"
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );

  if (showPreview) {
    return <PreviewForm />;
  }

  if (loadingTest) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading test data...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
            <div style={{ backgroundColor: '#ffc107', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCube} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Determination of Compressive Strength of AAC Block
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    This test is conducted to determine the compressive strength of AAC Blocks
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button 
                as={Link} 
                to="/other-services" 
                variant="light"
                className="btn-vitrag-primary"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Other Services
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Form onSubmit={handleSubmit}>
          {/* SECTION 1: General Information */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ color: '#FFA500' }} />
                          Customer *
                        </Form.Label>
                        {customersLoading ? (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" style={{ color: '#FFA500' }} />
                            <span style={{ color: '#e2e8f0' }}>Loading customers...</span>
                          </div>
                        ) : customersError ? (
                          <Alert variant="warning" className="mb-0">
                            {customersError}
                          </Alert>
                        ) : (
                          <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                            <Form.Control
                              type="text"
                              value={customerSearchTerm}
                              onChange={(e) => setCustomerSearchTerm(e.target.value)}
                              onClick={handleCustomerInputClick}
                              placeholder="Search and select customer..."
                              required
                              autoComplete="off"
                              readOnly
                              style={{
                                cursor: 'pointer',
                                backgroundColor: '#1C2333',
                                border: '2px solid #6c757d',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '12px 15px',
                                fontSize: '1rem'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#FFA500';
                                e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#6c757d';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            {showCustomerDropdown && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#1C2333',
                                  border: '2px solid #6c757d',
                                  borderTop: 'none',
                                  borderRadius: '0 0 8px 8px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  zIndex: 1000,
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                <div style={{ padding: '15px', borderBottom: '1px solid #6c757d' }}>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search customers..."
                                    value={dropdownSearchTerm}
                                    onChange={handleDropdownSearch}
                                    autoComplete="off"
                                    style={{
                                      backgroundColor: '#242B3C',
                                      border: '2px solid #6c757d',
                                      borderRadius: '8px',
                                      color: 'white',
                                      padding: '10px 12px',
                                      fontSize: '0.9rem'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#FFA500';
                                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#6c757d';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </div>
                                {filteredCustomers.length > 0 ? (
                                  filteredCustomers.map((customer) => (
                                    <div
                                      key={customer.id}
                                      onClick={() => handleCustomerSelect(customer)}
                                      style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #6c757d',
                                        color: 'white',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                        {customer.name}
                                      </div>
                                      <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                        {customer.phone && `${customer.phone} • `}
                                        {customer.city && customer.city}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div style={{ padding: '15px', color: '#adb5bd', textAlign: 'center' }}>
                                    {dropdownSearchTerm.trim() !== ''
                                      ? `No customers found matching "${dropdownSearchTerm}"`
                                      : 'No customers available'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Description *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="sample_description"
                          value={formData.sample_description}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Receipt *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_receipt"
                          value={formData.date_of_receipt}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Customer/Site Name & Address *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="customer_site_name_address"
                          value={formData.customer_site_name_address}
                          onChange={handleInputChange}
                          placeholder="Enter customer/site name and address"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="reference_number"
                          value={formData.reference_number}
                          onChange={handleInputChange}
                          placeholder="Enter reference number"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Manufacturer</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_manufacturer"
                          value={formData.date_of_manufacturer}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Type of Specimen *</Form.Label>
                        <Form.Control
                          type="text"
                          name="type_of_specimen"
                          value={formData.type_of_specimen}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Machine used for Testing *</Form.Label>
                        <Form.Control
                          type="text"
                          name="machine_used_for_testing"
                          value={formData.machine_used_for_testing}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Location of Test *</Form.Label>
                        <Form.Control
                          type="text"
                          name="location_of_test"
                          value={formData.location_of_test}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Capacity/Range *</Form.Label>
                        <Form.Control
                          type="text"
                          name="capacity_range"
                          value={formData.capacity_range}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Test Method *</Form.Label>
                        <Form.Control
                          type="text"
                          name="test_method"
                          value={formData.test_method}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 2: Sample Details */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faFlask} className="me-2" />
                    Sample Details
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Test Code Number *</Form.Label>
                        <Form.Control
                          type="text"
                          name="sample_test_code"
                          value={formData.sample_test_code}
                          onChange={handleInputChange}
                          placeholder="e.g., SCN-2024001"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Testing *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_testing"
                          value={formData.date_of_testing}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Report *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_report"
                          value={formData.date_of_report}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>ULR Number *</Form.Label>
                        <Form.Control
                          type="text"
                          name="ulr_number"
                          value={formData.ulr_number}
                          onChange={handleInputChange}
                          placeholder="Enter ULR number"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Job Code Number *</Form.Label>
                        <Form.Control
                          type="text"
                          name="job_code_number"
                          value={formData.job_code_number}
                          onChange={handleInputChange}
                          placeholder="Enter job code number"
                          required
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Manufacturer</Form.Label>
                        <Form.Control
                          type="text"
                          name="manufacturer"
                          value={formData.manufacturer}
                          onChange={handleInputChange}
                          placeholder="Enter manufacturer name"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Condition of Specimen</Form.Label>
                        <Form.Control
                          type="text"
                          name="condition_of_specimen"
                          value={formData.condition_of_specimen}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Calibration Due Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="calibration_due_date"
                          value={formData.calibration_due_date}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Environmental condition</Form.Label>
                        <Form.Control
                          type="text"
                          name="environmental_condition"
                          value={formData.environmental_condition}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 3: AAC Block Specific Details */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faCube} className="me-2" />
                    AAC Block Specific Details
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Quantity of Blocks *</Form.Label>
                        <Form.Control
                          type="text"
                          name="quantity_of_blocks"
                          value={formData.quantity_of_blocks}
                          onChange={handleInputChange}
                          placeholder="e.g., 10"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Casting *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_casting"
                          value={formData.date_of_casting}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Grade of Blocks *</Form.Label>
                        <Form.Control
                          type="text"
                          name="grade_of_blocks"
                          value={formData.grade_of_blocks}
                          onChange={handleInputChange}
                          placeholder="e.g., Grade 3.5"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Manufacture of Blocks *</Form.Label>
                        <Form.Control
                          type="text"
                          name="manufacture_of_blocks"
                          value={formData.manufacture_of_blocks}
                          onChange={handleInputChange}
                          placeholder="e.g., ABC Manufacturing"
                          required
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Blocks Condition *</Form.Label>
                        <Form.Control
                          type="text"
                          name="blocks_condition"
                          value={formData.blocks_condition}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Curing Condition *</Form.Label>
                        <Form.Control
                          type="text"
                          name="curing_condition"
                          value={formData.curing_condition}
                          onChange={handleInputChange}
                          placeholder="e.g., Air cured, Water cured"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 4: Test Results Table */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="d-md-none text-muted small mb-2" style={{ fontSize: '0.8rem' }}>
                    Scroll horizontally to see all columns →
                  </p>
                  <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '0' }}>
                    <table className="table transparent-table" style={{ fontSize: '0.9rem', width: '100%', minWidth: '960px', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
                      <thead className="table-dark">
                        <tr>
                          <th rowSpan="2" style={{ width: '50px', minWidth: '50px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Sr. No</th>
                          <th rowSpan="2" style={{ width: '90px', minWidth: '90px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Blocks ID</th>
                          <th colSpan="3" style={{ padding: '10px', border: '1px solid #6c757d', textAlign: 'center', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Size</th>
                          <th rowSpan="2" style={{ width: '90px', minWidth: '90px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Area (mm²)</th>
                          <th rowSpan="2" style={{ width: '120px', minWidth: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Block Weight (Kg)</th>
                          <th rowSpan="2" style={{ width: '120px', minWidth: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Density (Kg/m³)</th>
                          <th rowSpan="2" style={{ width: '120px', minWidth: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Max Load (N)</th>
                          <th rowSpan="2" style={{ width: '150px', minWidth: '120px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>Comp. Strength (MPa)</th>
                        </tr>
                        <tr>
                          <th style={{ width: '80px', minWidth: '72px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>L (mm)</th>
                          <th style={{ width: '80px', minWidth: '72px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>B (mm)</th>
                          <th style={{ width: '80px', minWidth: '72px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700', whiteSpace: 'nowrap' }}>H (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((row) => (
                          <tr key={row} style={{ backgroundColor: '#1C2333' }}>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#1C2333', color: 'white' }}>{row}</td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="text"
                                name={`block_id_${row}`}
                                value={formData[`block_id_${row}`]}
                                onChange={handleInputChange}
                                placeholder=""
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', minWidth: '72px' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`length_${row}`}
                                value={formData[`length_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateArea(row), 0);
                                }}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', minWidth: '56px' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', minWidth: '72px' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`breadth_${row}`}
                                value={formData[`breadth_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateArea(row), 0);
                                }}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', minWidth: '56px' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', minWidth: '72px' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`height_${row}`}
                                value={formData[`height_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateDensity(row), 0);
                                }}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', minWidth: '56px' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', textAlign: 'center' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`area_${row}`}
                                value={formData[`area_${row}`]}
                                readOnly
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.001"
                                name={`weight_${row}`}
                                value={formData[`weight_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateDensity(row), 0);
                                }}
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`density_${row}`}
                                value={formData[`density_${row}`]}
                                readOnly
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`load_max_${row}`}
                                value={formData[`load_max_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateAverages(), 0);
                                }}
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`compressive_strength_${row}`}
                                value={formData[`compressive_strength_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateAverages(), 0);
                                }}
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                          </tr>
                        ))}
                        {/* Average Row */}
                        <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                          <td colSpan="8" className="text-center fw-bold" style={{ padding: '10px', border: '1px solid #6c757d', fontSize: '1rem', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>Average</td>
                          <td className="text-center fw-bold" style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.1"
                              name="avg_load_max"
                              value={formData.avg_load_max}
                              readOnly
                              className="average-input-transparent"
                              style={{ 
                                fontSize: '0.9rem',
                                backgroundColor: '#495057',
                                border: '1px solid #6c757d',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: '700',
                                cursor: 'not-allowed'
                              }}
                            />
                          </td>
                          <td className="text-center fw-bold" style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              name="avg_compressive_strength"
                              value={formData.avg_compressive_strength}
                              readOnly
                              className="average-input-transparent"
                              style={{ 
                                fontSize: '0.9rem',
                                backgroundColor: '#495057',
                                border: '1px solid #6c757d',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: '700',
                                cursor: 'not-allowed'
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 4b: Block Images (for Test Report Annexure) */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faImage} className="me-2" />
                    Block Images (for Test Report)
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-4">
                    Add Front side failure, Digital reading, and Back side failure photos for each block. These appear in the Test Report annexure.
                  </p>
                  {[1, 2, 3].map((blockNum) => (
                    <Card key={blockNum} className="mb-4 border-secondary" style={{ backgroundColor: '#1C2333' }}>
                      <Card.Header className="py-2" style={{ backgroundColor: '#495057', color: 'white', fontWeight: '600' }}>
                        Block {blockNum}
                      </Card.Header>
                      <Card.Body className="p-4">
                        <Row className="g-3">
                          {/* Front Side Failure */}
                          <Col md={4}>
                            <div className="text-center mb-2">
                              <h6 className="text-white fw-semibold mb-0" style={{ fontSize: '0.9rem' }}>Front Side Failure</h6>
                            </div>
                            <div className="border border-secondary rounded-3 bg-dark" style={{ height: '160px', position: 'relative', overflow: 'hidden' }}>
                              {blockImages[`front_failure_${blockNum}`] ? (
                                <>
                                  <img
                                    src={blockImages[`front_failure_${blockNum}`]}
                                    alt={`Block ${blockNum} Front`}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteBlockImage(`front_failure_${blockNum}`)}
                                    style={{ position: 'absolute', top: '6px', right: '6px', padding: '4px 8px', zIndex: 10 }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </>
                              ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                  <FontAwesomeIcon icon={faImage} className="fa-2x mb-2" />
                                  <span>No photo</span>
                                </div>
                              )}
                            </div>
                            <div className="d-grid gap-2 mt-2">
                              <Button
                                variant="warning"
                                size="sm"
                                className="fw-semibold"
                                onClick={() => openAacCamera(`front_failure_${blockNum}`)}
                              >
                                <FontAwesomeIcon icon={faCamera} className="me-2" />
                                Capture
                              </Button>
                              <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => document.getElementById(`aac-upload-front-${blockNum}`).click()}
                              >
                                <FontAwesomeIcon icon={faUpload} className="me-2" />
                                Upload
                              </Button>
                            </div>
                            <input
                              type="file"
                              id={`aac-upload-front-${blockNum}`}
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleBlockImageUpload(blockNum, 'front_failure', file);
                                e.target.value = '';
                              }}
                            />
                          </Col>
                          {/* Digital Reading */}
                          <Col md={4}>
                            <div className="text-center mb-2">
                              <h6 className="text-white fw-semibold mb-0" style={{ fontSize: '0.9rem' }}>Digital Reading</h6>
                            </div>
                            <div className="border border-secondary rounded-3 bg-dark" style={{ height: '160px', position: 'relative', overflow: 'hidden' }}>
                              {blockImages[`digital_reading_${blockNum}`] ? (
                                <>
                                  <img
                                    src={blockImages[`digital_reading_${blockNum}`]}
                                    alt={`Block ${blockNum} Digital`}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteBlockImage(`digital_reading_${blockNum}`)}
                                    style={{ position: 'absolute', top: '6px', right: '6px', padding: '4px 8px', zIndex: 10 }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </>
                              ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                  <FontAwesomeIcon icon={faImage} className="fa-2x mb-2" />
                                  <span>No photo</span>
                                </div>
                              )}
                            </div>
                            <div className="d-grid gap-2 mt-2">
                              <Button
                                variant="warning"
                                size="sm"
                                className="fw-semibold"
                                onClick={() => openAacCamera(`digital_reading_${blockNum}`)}
                              >
                                <FontAwesomeIcon icon={faCamera} className="me-2" />
                                Capture
                              </Button>
                              <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => document.getElementById(`aac-upload-digital-${blockNum}`).click()}
                              >
                                <FontAwesomeIcon icon={faUpload} className="me-2" />
                                Upload
                              </Button>
                            </div>
                            <input
                              type="file"
                              id={`aac-upload-digital-${blockNum}`}
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleBlockImageUpload(blockNum, 'digital_reading', file);
                                e.target.value = '';
                              }}
                            />
                          </Col>
                          {/* Back Side Failure */}
                          <Col md={4}>
                            <div className="text-center mb-2">
                              <h6 className="text-white fw-semibold mb-0" style={{ fontSize: '0.9rem' }}>Back Side Failure</h6>
                            </div>
                            <div className="border border-secondary rounded-3 bg-dark" style={{ height: '160px', position: 'relative', overflow: 'hidden' }}>
                              {blockImages[`back_failure_${blockNum}`] ? (
                                <>
                                  <img
                                    src={blockImages[`back_failure_${blockNum}`]}
                                    alt={`Block ${blockNum} Back`}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteBlockImage(`back_failure_${blockNum}`)}
                                    style={{ position: 'absolute', top: '6px', right: '6px', padding: '4px 8px', zIndex: 10 }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </>
                              ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                  <FontAwesomeIcon icon={faImage} className="fa-2x mb-2" />
                                  <span>No photo</span>
                                </div>
                              )}
                            </div>
                            <div className="d-grid gap-2 mt-2">
                              <Button
                                variant="warning"
                                size="sm"
                                className="fw-semibold"
                                onClick={() => openAacCamera(`back_failure_${blockNum}`)}
                              >
                                <FontAwesomeIcon icon={faCamera} className="me-2" />
                                Capture
                              </Button>
                              <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => document.getElementById(`aac-upload-back-${blockNum}`).click()}
                              >
                                <FontAwesomeIcon icon={faUpload} className="me-2" />
                                Upload
                              </Button>
                            </div>
                            <input
                              type="file"
                              id={`aac-upload-back-${blockNum}`}
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleBlockImageUpload(blockNum, 'back_failure', file);
                                e.target.value = '';
                              }}
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 5: Verification */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Verification
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={4}>
                      <h6>Tested By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="tested_by_name"
                          value={formData.tested_by_name}
                          onChange={handleInputChange}
                          placeholder="Enter tester name"
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control
                          type="date"
                          name="tested_by_date"
                          value={formData.tested_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Checked By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="checked_by_name"
                          value={formData.checked_by_name}
                          onChange={handleInputChange}
                          placeholder="Enter checker name"
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control
                          type="date"
                          name="checked_by_date"
                          value={formData.checked_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Verified By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="verified_by_name"
                          value={formData.verified_by_name}
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control
                          type="date"
                          name="verified_by_date"
                          value={formData.verified_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 6: Remarks */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faComment} className="me-2" />
                    Remarks
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder="Enter any additional remarks or observations..."
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 7: Review & Authorization */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Reviewed By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Select Reviewer:</Form.Label>
                    <Form.Select 
                      name="reviewed_by" 
                      value={formData.reviewed_by}
                      onChange={handleInputChange}
                      required
                      style={{
                        backgroundColor: '#495057',
                        border: '2px solid #6c757d',
                        borderRadius: '8px',
                        color: 'white',
                        padding: '12px 15px',
                        fontSize: '1rem'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FFA500';
                        e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#6c757d';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" style={{ backgroundColor: '#495057', color: 'white' }}>-- Select a reviewer --</option>
                      <option value="Lalita S. Dussa - Quality Manager" style={{ backgroundColor: '#495057', color: 'white' }}>Lalita S. Dussa - Quality Manager</option>
                      <option value="Prakarsha A. Sangave - Quality Manager" style={{ backgroundColor: '#495057', color: 'white' }}>Prakarsha A. Sangave - Quality Manager</option>
                      <option value="Harsha Prakarsha Sangave - Quality Manager" style={{ backgroundColor: '#495057', color: 'white' }}>Harsha Prakarsha Sangave - Quality Manager</option>
                      <option value="Amol A Adam - Quality Manager" style={{ backgroundColor: '#495057', color: 'white' }}>Amol A Adam - Quality Manager</option>
                      <option value="Aaquib J. Shaikh - Quality Manager" style={{ backgroundColor: '#495057', color: 'white' }}>Aaquib J. Shaikh - Quality Manager</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Authorized By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Authorized By:</Form.Label>
                    <Form.Control
                      type="text"
                      name="authorized_by"
                      value={formData.authorized_by}
                      readOnly
                      style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons – only Save while filling form */}
          <Row>
            <Col className="text-center">
              <Button 
                type="button"
                variant="primary" 
                size="lg"
                onClick={handleSaveTestData}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Test Data
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      {/* Loading overlay when saving (like Liquid Admixture) */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <Spinner animation="border" variant="primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div className="mt-3">
              <h5 className="text-dark mb-2">Saving Test Data...</h5>
              <p className="text-muted mb-0">Please wait while we save your data to the database</p>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal - same as cube testing */}
      <div
        id="aacCameraModal"
        style={{
          display: 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.8)',
          zIndex: 9999,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <h5 className="mb-3">Capture Photo</h5>
          {isCapturing ? (
            <div className="py-5">
              <div className="spinner-border text-primary mb-3" role="status" />
              <p className="text-muted mb-0">Please wait while we process your photo</p>
            </div>
          ) : (
            <>
              <video
                id="aacCameraVideo"
                width="400"
                height="300"
                style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                autoPlay
                playsInline
                muted
              />
              <canvas id="aacCameraCanvas" style={{ display: 'none' }} />
              <div className="mt-3 d-flex gap-2 justify-content-center flex-wrap">
                <Button variant="info" onClick={switchAacCamera}>
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Switch Camera
                </Button>
                <Button variant="primary" onClick={captureAacPhoto}>
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Capture
                </Button>
                <Button variant="secondary" onClick={closeAacCamera}>
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AACBlocksForm;
